import Button from "@/components/common/Button";
import { useCourse } from "@/hooks/queries/useCourseQueries";
import { useTutorial } from "@/hooks/queries/useTutorialQueries";
import { useAuth } from "@/hooks/useAuth";
import { useQuiz } from "@/hooks/useQuiz";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import CourseHero from "@/components/course/CourseHero";
import TutorialCard from "@/components/course/TutorialCard";
import TutorialModal from "@/components/course/TutorialModal";
import { getLoginType } from "@/utils/secureStorage";

const CourseDetails = () => {
  const { currentUser: user } = useAuth();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: course, isLoading, error } = useCourse(id || "");

  // State for tutorial modal
  const [selectedTutorial, setSelectedTutorial] = useState<any>(null);
  
  // Get complete tutorial data with quizzes
  const { data: fetchedTutorial } = useTutorial(selectedTutorial?._id || "");
  
  // Quiz state management using custom hook
  const {
    showQuiz,
    quiz,
    quizError,
    quizAnswers,
    quizResult,
    progress,
    currentQuizQuestionIdx,
    isSubmittingQuiz,
    fetchQuiz,
    fetchProgress,
    checkQuizResult,
    handleQuizAnswer,
    handleQuizSubmit,
    startQuiz,
    resetQuiz,
    goToPreviousQuestion,
    goToNextQuestion,
  } = useQuiz();

  // Get tutorials from the course data
  const tutorials = course?.document?.tutorials || [];
  const language = i18n.language;

  // Debug: Log course structure to see where quizzes might be
  console.log("Course data structure:", course);
  console.log("Course tutorials:", tutorials);

  // Get user's tutorial progress for this course
  const getUserTutorialProgress = (tutorialId: string) => {
    if (!(user as any)?.learningProgress) {
      return null;
    }

    const courseProgress = (user as any).learningProgress.find(
      (course: any) => course.courseId === id
    );

    if (!courseProgress) {
      return null;
    }

    const tutorialProgress = courseProgress.tutorials?.find(
      (tutorial: any) => tutorial.tutorialId === tutorialId
    );

    if (!tutorialProgress) {
      return null;
    }

    return {
      progress: tutorialProgress.progress || 0,
      isCompleted: tutorialProgress.isCompleted || false,
      isInProgress: tutorialProgress.isInProgress || false,
      lastWatchedAt: tutorialProgress.lastWatchedAt,
    };
  };

  // Open tutorial modal
  const openTutorialModal = async (tutorial: any) => {
    setSelectedTutorial(tutorial);
    resetQuiz();
    fetchProgress(course?.document?._id, tutorial._id);
    
    // Check if user has already taken the quiz
    const hasTakenQuiz = await checkQuizResult(course?.document?._id, tutorial._id);
    if (hasTakenQuiz) {
      console.log("User has already taken this quiz");
    }
  };

  // Close tutorial modal
  const closeTutorialModal = () => {
    setSelectedTutorial(null);
    resetQuiz();
  };

  // Handle quiz submission with proper parameters
  const handleQuizSubmitClick = () => {
    console.log("Submit button clicked!");
    console.log("Quiz answers before submit:", quizAnswers);
    console.log("Quiz object before submit:", quiz);
    handleQuizSubmit(user, course, selectedTutorial);
  };

  // Handle take quiz button click
  const handleTakeQuiz = async () => {
    if (!selectedTutorial) return;
    
    console.log("Take Quiz button clicked");
    console.log("Full selectedTutorial object:", selectedTutorial);
    console.log("Full fetchedTutorial object:", fetchedTutorial);
    console.log("fetchedTutorial.quizzes:", fetchedTutorial?.quizzes);
    console.log("fetchedTutorial keys:", fetchedTutorial ? Object.keys(fetchedTutorial) : "No fetchedTutorial");

    // Check if user has already taken the quiz
    const hasTakenQuiz = await checkQuizResult(course?.document?._id || "", selectedTutorial._id);
    if (hasTakenQuiz) {
      console.log("User has already taken this quiz, showing previous result");
      return;
    }

    // Use fetchedTutorial data which should have complete tutorial details including quizzes
    if (fetchedTutorial?.quizzes && fetchedTutorial.quizzes.length > 0) {
      console.log("Starting quiz with data from fetchedTutorial:", fetchedTutorial.quizzes[0]);
      startQuiz(fetchedTutorial.quizzes[0]);
        } else {
      console.log("No quiz data found in fetchedTutorial, trying selectedTutorial");
      if (selectedTutorial.quizzes && selectedTutorial.quizzes.length > 0) {
        console.log("Starting quiz with data from selectedTutorial:", selectedTutorial.quizzes[0]);
        startQuiz(selectedTutorial.quizzes[0]);
      } else {
        console.log("No quiz data found in either tutorial, fetching from API");
        fetchQuiz(course?.document?._id || "", selectedTutorial._id);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !course) {
    const loginType = getLoginType();
    const isCompanyAdmin = loginType === "company" || user?.role === "company_admin";
    
    if (isCompanyAdmin && error) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center py-24">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("auth.accessRestricted")}
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            {t("auth.loginAsUserPlease")}
          </p>
          <Button
            variant="primary"
            className="mt-6"
            onClick={() => navigate("/login")}
          >
            {t("auth.goToLogin")}
          </Button>
        </div>
      );
    }
    
    return (
      <div className="flex h-full w-full flex-col items-center justify-center py-24">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t("common.courseNotFound")}
        </h2>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          {t("common.courseNotFoundMessage")}
        </p>
        <Button
          variant="primary"
          className="mt-6"
          onClick={() => navigate("/courses")}
        >
          {t("common.backToCourses")}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center space-x-2 sm:space-x-4">
          <button
            onClick={() => navigate("/courses")}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
              <ArrowLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
              {t("courses.courseDetails")}
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl shadow-lg overflow-hidden">
          {/* Course Hero Section */}
          <CourseHero course={course} language={language} />

          {/* Tutorials Section */}
          <div className="p-4 sm:p-8">
            <div className="mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {t("courses.tutorials")}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                Explore the course content and start learning
              </p>
            </div>

            {tutorials.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {tutorials.map((tutorial: any, index: number) => (
                  <div key={tutorial._id} className="w-full">
                    <TutorialCard
                      tutorial={tutorial}
                      language={language}
                      getUserTutorialProgress={getUserTutorialProgress}
                      onTutorialClick={openTutorialModal}
                      partNumber={index + 1}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 text-gray-400">
                <svg
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                    className="w-full h-full"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                      </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {t("courses.noTutorialsAvailable")}
                                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {t("courses.noTutorialsDescription")}
                </p>
                              </div>
                            )}
                          </div>
                                </div>
                              </div>

      {/* Tutorial Modal */}
      {selectedTutorial && (
        <TutorialModal
          selectedTutorial={selectedTutorial}
          onClose={closeTutorialModal}
          language={language}
          // Quiz-related props
          showQuiz={showQuiz}
          quiz={quiz}
          quizAnswers={quizAnswers}
          currentQuizQuestionIdx={currentQuizQuestionIdx}
          isSubmittingQuiz={isSubmittingQuiz}
          quizError={quizError}
          quizResult={quizResult}
          progress={progress}
          onAnswerChange={handleQuizAnswer}
          onPreviousQuestion={goToPreviousQuestion}
          onNextQuestion={goToNextQuestion}
          onQuizSubmitClick={handleQuizSubmitClick}
          onTakeQuiz={handleTakeQuiz}
        />
      )}
                                </div>
  );
};

export default CourseDetails;
